import { useEffect } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { releaseUnloadedKeySlot } from '../../../../../API/GoMoto';
import { useAppointmentState } from '../../../../../context/Appointment/appointment';
import { useLocalization, useLocale } from '../../../../../context/Localization/localization';
import { useSetHeaderProps, useSetKioskBannerVisibility } from '../../../../../context/Page/page';
import { GMAppointmentName } from '../../../../../controls/AppointmentName';
import { AppointmentQRCode } from '../../../../../controls/AppointmentQRCode/AppointmentQRCode';
import { GMUppercase } from '../../../../../controls/Case';
import { IconButton } from '../../../../../controls/IconButton/IconButton';
import { useFormatAppointmentDate } from '../../../../../helpers/Appointment';
import { ScrollableBody } from '../../../../Layout/ScrollableBody/ScrollableBody';
import css from './FinishWithAdvisor.module.scss';
import { useIsLockerOpComplete } from '../IsLockerOpComplete';
import { textCapitalize } from '../../../../../helpers/textCapitalize';
import { TransportSubtype, TransportType } from '../../../../../API/Enums';
import { FinishWithAdvisorProps, GetTransportType, TransportMap } from './FinishWithAdvisor.types';
import { IAppointment } from '../../../../../API/GoMoto';
import { useTransportLabels } from '../../../../../API/Labels/useTransportLabels';
import { GMSizedIcon } from '../../../../../controls/Icon';

/**
 * The `See Advisor` screen for 365.
 *
 * @param onAdvisorCompleted - {@link FinishWithAdvisorProps.onAdvisorCompleted onAdvisorCompleted} description.
 */
export const FinishWithAdvisor = ({
    onAdvisorCompleted,
}: FinishWithAdvisorProps) => {
    const Localization = useLocalization();
    const locale = useLocale();
    const setHeader = useSetHeaderProps();
    const showBanner = useSetKioskBannerVisibility();
    const navigate: NavigateFunction = useNavigate();
    const { lockerNumber, complete, kioskKey, id } = useIsLockerOpComplete(-1, true);
    const appointment: IAppointment = useAppointmentState();
    const formatAppointmentDate = useFormatAppointmentDate();
    const { getLoanerOrRentalName } = useTransportLabels();

    const transportMap: TransportMap = {
        [TransportType.drop]: {
            [TransportSubtype.none]: Localization.DroppingOff,
        },
        [TransportType.shuttle]: {
            [TransportSubtype.shuttle]: Localization.formatString(Localization.Request, Localization.Shuttle) as string,
            [TransportSubtype.enterprise]: `Enterprise ${Localization.RentalShuttle}`,
            [TransportSubtype.lyft]: Localization.formatString(Localization.Request, 'Lyft') as string,
            [TransportSubtype.uber]: Localization.formatString(Localization.Request, 'Uber') as string,
        },
        [TransportType.wait]: {
            [TransportSubtype.none]: Localization.WaitHere,
        },
        [TransportType.loaner]: {
            [TransportSubtype.none]: getLoanerOrRentalName(appointment.transportation?.dmsTransportationType ?? ''),
        },
        [TransportType.notNeeded]: {
            [TransportSubtype.none]: Localization.NotNeeded,
        },
    };

    const getTransportType: GetTransportType = () => {
        if (!appointment.transportation) {
            return null;
        }

        const transportType: TransportType = appointment.transportation.type;
        const transportSubtype: TransportSubtype = appointment.transportation.subtype;

        return transportMap[transportType][transportSubtype] ?? null;
    };

    useEffect(() => {
        setHeader({
            title: Localization.FinishWithAdvisor,
            rightIcons: [
                <IconButton
                    key={'em-sr-close'}
                    sizedIcon={
                        <GMSizedIcon
                            iconType={'Close'}
                            iconSize={'medium'}
                            ariaLabel={Localization.CloseIcon}
                            title={Localization.ClickToCloseWindow}
                        />
                    }
                    onClick={() => navigate(-1)}
                    ariaLabel={Localization.CloseIcon}
                    id={Localization.CloseIcon}
                />,
            ],
        });
        showBanner(false);

        return () => {
            setHeader(undefined);
            showBanner(true);
        };

    }, [setHeader, showBanner, navigate, Localization, locale]);

    useEffect(() => {
        if (complete && appointment.allowManualComplete) {
            onAdvisorCompleted(kioskKey);
        }
    }, [complete, onAdvisorCompleted, appointment.allowManualComplete, kioskKey, id]);

    useEffect(() => {
        if (appointment.key && appointment.keySlotNumber) {
            releaseUnloadedKeySlot(appointment.key)
                .catch((_) => null /* suppress reject message */);
        }
    }, [appointment.key, appointment.keySlotNumber]);

    return (
        <ScrollableBody>
            <h2 className={css.title}>
                {Localization.CheckInDetailsSaved}
            </h2>
            <p>
                {Localization.CheckInFinishWithAdvisor}
            </p>
            <p className={css.advisor}>
                {Localization.formatString(Localization.Advisor, <b>{textCapitalize(appointment.serviceAdvisorName)}</b>)}
            </p>
            <h4>
                <div>
                    <GMAppointmentName />
                </div>
                <GMUppercase>
                    {formatAppointmentDate(appointment)}
                </GMUppercase>
                <div>
                    {getTransportType()}
                </div>
                <div>
                    {!!appointment.tagNumber && Localization.formatString(Localization.KeyTagNumberDisplay, appointment.tagNumber)}
                </div>
            </h4>
            <AppointmentQRCode
                mobileCheckInComplete
                email={false}
                lockerNumber={lockerNumber}
            />
        </ScrollableBody >
    );
};
